import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  applyVisualFix,
  discardVisualFix,
  listVisualFixes,
  requestVisualFix,
  revertVisualFix,
} from '@platform/client';
import { visualFixesSchema } from '@platform/schema/visual-fix';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';
import { reviewQuery } from '../library/sources';

import { ChangeTable } from './ChangeTable';

type Verb = 'apply' | 'discard' | 'revert';

const CALL = {
  apply: applyVisualFix,
  discard: discardVisualFix,
  revert: revertVisualFix,
};

/**
 * Fix with AI on one scene's characters and props — GEN-135. A request, the diff it
 * proposes with Apply and Cancel, then Revert once applied.
 */
export function VisualFixFlow({
  lessonId,
  sceneId,
  disabled,
}: {
  lessonId: string;
  sceneId: string;
  disabled: boolean;
}) {
  const queries = useQueryClient();
  // Under `review`, so the workspace's refresh after any write reads it again.
  const fixes = useQuery({
    queryKey: ['review', lessonId, 'visual-fixes'],
    queryFn: async () =>
      visualFixesSchema.parse(
        (await listVisualFixes({ path: { id: lessonId }, throwOnError: true }))
          .data,
      ).fixes,
  });
  const [request, setRequest] = useState('');
  const refresh = () =>
    queries.invalidateQueries({ queryKey: reviewQuery(lessonId).queryKey });

  const ask = useTrackedMutation('Fix with AI', {
    mutationFn: async () => {
      await requestVisualFix({
        path: { id: sceneId },
        body: { lessonId, request },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      setRequest('');
      await refresh();
    },
  });
  const decide = useTrackedMutation('Decide on the fix', {
    mutationFn: async ({ verb, id }: { verb: Verb; id: string }) => {
      await CALL[verb]({ path: { id }, throwOnError: true });
    },
    onSuccess: refresh,
  });

  // The newest fix on this scene, while there is still something to do with it.
  const fix = fixes.data?.find((one) => one.sceneId === sceneId);
  const open =
    fix?.status === 'PROPOSED' || fix?.status === 'APPLIED' ? fix : undefined;
  const busy = disabled || ask.isPending || decide.isPending;
  const failure = ask.error ?? decide.error;
  const act = (verb: Verb, text: string, primary = false) =>
    open === undefined ? null : (
      <Button
        key={verb}
        size="small"
        variant={primary ? 'contained' : 'outlined'}
        disabled={busy}
        onClick={() => {
          decide.mutate({ verb, id: open.id });
        }}
      >
        {text}
      </Button>
    );

  return (
    <Stack spacing={1} data-testid="visual-fix">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle2" component="h4">
          Fix with AI
        </Typography>
        <Typography variant="caption" color="text.secondary">
          This scene
        </Typography>
      </Stack>

      {open?.status === 'PROPOSED' && (
        <Alert severity="warning">
          Fix with AI proposes the change below to this scene’s picture. Nothing
          changes until you apply it.
          <Typography variant="body2">Why: {open.why}</Typography>
        </Alert>
      )}
      {open?.status === 'APPLIED' && (
        <Alert severity="success">
          Applied “{open.request}”. Revert puts back only these fields.
        </Alert>
      )}
      {open !== undefined && <ChangeTable changes={open.changes} />}

      {(ask.isPending || decide.isPending) && <LinearProgress />}
      {failure !== null && (
        <Alert severity="error">{describeApiError(failure)}</Alert>
      )}

      {open?.status === 'PROPOSED' ? (
        <Stack direction="row" spacing={1}>
          {act('apply', 'Apply', true)}
          {act('discard', 'Cancel')}
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <TextField
            size="small"
            fullWidth
            label="What should change in this scene’s picture?"
            placeholder="Make Rosa look thoughtful and add a menu beside her"
            value={request}
            disabled={busy}
            onChange={(event) => {
              setRequest(event.target.value);
            }}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={busy || request.trim().length === 0}
            onClick={() => {
              ask.mutate();
            }}
          >
            Fix with AI
          </Button>
          {open?.status === 'APPLIED' && act('revert', 'Revert')}
        </Stack>
      )}
    </Stack>
  );
}
